import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'commitment', label: 'Our Commitment' },
  { id: 'scope', label: 'Scope' },
  { id: 'report', label: 'How to Report' },
  { id: 'include', label: 'What to Include' },
  { id: 'promise', label: 'What We Promise' },
  { id: 'rules', label: 'Rules for Researchers' },
  { id: 'safeharbor', label: 'Safe Harbor' },
  { id: 'outofscope', label: 'Out-of-Scope Findings' },
  { id: 'recognition', label: 'Recognition' },
];

const VulnerabilityDisclosure = () => (
  <LegalPageLayout title="Vulnerability Disclosure Policy" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="commitment" number={1} title="Our Commitment">
      <p>
        BookingFlows processes bookings, payments, and personal data for real businesses — security is
        core to what we sell. We welcome good-faith security research and appreciate every report that
        helps us protect Operators and their customers. This policy explains what is in scope, how to
        report, and what you can expect from us.
      </p>
    </LegalSection>

    <LegalSection id="scope" number={2} title="Scope">
      <p>The following are <strong>in scope</strong>:</p>
      <ul>
        <li><strong>bookingflows.com</strong> — the web application and dashboard;</li>
        <li><strong>api.bookingflows.com</strong> — public and authenticated APIs;</li>
        <li>Embeddable booking widgets and customer-facing pages (tickets, waivers, checkout).</li>
      </ul>
      <p>The following are <strong>out of scope</strong>:</p>
      <ul>
        <li>Third-party services we integrate with (Stripe, Twilio, QuickBooks, etc.) — report those to the vendor directly;</li>
        <li>Social engineering, phishing, or physical attacks against BookingFlows personnel or Operators;</li>
        <li>Denial-of-service testing of any kind;</li>
        <li>Automated scanning that generates significant traffic against production systems.</li>
      </ul>
    </LegalSection>

    <LegalSection id="report" number={3} title="How to Report">
      <LegalCallout>
        <p>
          Report vulnerabilities through our{' '}
          <a href="/support?category=Security%20%26%20Vulnerability%20Reports#tickets">
            Support Center
          </a>{' '}
          using the <strong>&ldquo;Security &amp; Vulnerability Reports&rdquo;</strong> category. Please do not
          post exploit details publicly before we have had a reasonable opportunity to remediate.
        </p>
      </LegalCallout>
    </LegalSection>

    <LegalSection id="include" number={4} title="What to Include">
      <ul>
        <li>A description of the vulnerability and its potential impact;</li>
        <li>Step-by-step reproduction instructions (URLs, endpoints, request/response samples);</li>
        <li>The environment or account context in which it was observed;</li>
        <li>Any proof-of-concept material — screenshots or minimal scripts are welcome;</li>
        <li>How you would like to be credited, if the report is validated (optional).</li>
      </ul>
    </LegalSection>

    <LegalSection id="promise" number={5} title="What We Promise">
      <ul>
        <li>We will acknowledge your report promptly — typically within a few business days;</li>
        <li>We will investigate every good-faith report and keep you informed of our progress;</li>
        <li>We will remediate validated vulnerabilities with urgency proportional to their severity;</li>
        <li>We will not take legal action against researchers who follow this policy (see Safe Harbor below).</li>
      </ul>
    </LegalSection>

    <LegalSection id="rules" number={6} title="Rules for Researchers">
      <ul>
        <li>Do not access, modify, or delete data belonging to other users or Operators — use your own test accounts;</li>
        <li>Stop and report immediately if you encounter personal data that is not your own;</li>
        <li>Do not degrade, disrupt, or damage the service;</li>
        <li>Do not exfiltrate data beyond the minimum needed to demonstrate the issue;</li>
        <li>Give us a reasonable remediation window before any public disclosure;</li>
        <li>Comply with all applicable laws.</li>
      </ul>
    </LegalSection>

    <LegalSection id="safeharbor" number={7} title="Safe Harbor">
      <p>
        Security research conducted in good faith and in accordance with this policy is considered
        authorized. We will not initiate legal action against you for such research, and if a third
        party initiates action, we will make it known that your activities were conducted in compliance
        with this policy. This safe harbor does not apply to research that violates the rules above.
      </p>
    </LegalSection>

    <LegalSection id="outofscope" number={8} title="Out-of-Scope Findings">
      <p>The following report types are generally not accepted without a demonstrated exploit:</p>
      <ul>
        <li>Missing security headers or cookie flags without a demonstrated impact;</li>
        <li>Software version disclosure or banner information;</li>
        <li>Reports from automated scanners without validation;</li>
        <li>Rate-limiting observations without demonstrated abuse impact;</li>
        <li>Clickjacking on pages with no sensitive state-changing actions;</li>
        <li>Best-practice recommendations without a concrete vulnerability.</li>
      </ul>
    </LegalSection>

    <LegalSection id="recognition" number={9} title="Recognition">
      <p>
        We do not currently operate a paid bug bounty program. Validated reports receive our sincere
        thanks, direct communication with the team fixing the issue, and — with your permission —
        credit for the discovery.
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default VulnerabilityDisclosure;
